import { defineNuxtRouteMiddleware, useResponseHeader } from '#imports'

import type { Owner } from '@backmarket/nuxt-module-logger/types'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { findRouteOrParentMatchingPredicate } from '@backmarket/utils/route/findRouteOrParentMatchingPredicate'

export default defineNuxtRouteMiddleware((to) => {
  const logger = useLogger()

  // Skip middleware execution on client-side
  // Cache headers only need to be set during server-side rendering
  if (import.meta.client) return

  const cacheRule = to.meta.cache

  // Walk the matched routes from child to parent to find the most specific
  // route that defines `authenticated`, mirroring the logic in the
  // authenticated middleware (nuxt-layer-oauth). This ensures we correctly
  // detect authentication requirements inherited from parent routes.
  const routeWithAuth = findRouteOrParentMatchingPredicate(to, (route) =>
    Boolean(route.meta?.authenticated),
  )

  // A route requires authentication if `authenticated` is defined on it (or a
  // parent) and `required` is not explicitly `false`.
  const isAuthRequired =
    routeWithAuth !== undefined &&
    routeWithAuth.meta?.authenticated?.required !== false

  // Scenario 1: Authenticated route has cache defined - this is a mistake
  if (isAuthRequired && cacheRule) {
    logger.error(
      `Route "${to.path}" requires authentication but has cache rule defined. Cache rules should NOT be set on authenticated routes. The cache header will not be applied.`,
      { owners: to.meta.owners as Owner[] },
    )

    // Exit without setting cache header
    // Note: Fallback configuration may exist in Cloudflare, contact infra team for more information
    return
  }

  // Scenario 2: Public route with no cache rule - gentle reminder
  if (!isAuthRequired && !cacheRule) {
    // Will be uncomment with "Log info for public routes without cache meta"
    // https://backmarket.atlassian.net/browse/B2CS-1125
    // logger.info(
    //   `Route "${to.path}" is public but has no cache rule defined. Consider adding a cache rule to improve performance.`,
    //   { owners: to.meta.owners as Owner[] },
    // )

    // Exit without setting cache header
    // Note: Fallback configuration may exist in Cloudflare, contact infra team for more information
    return
  }

  // Normal case: Public route with cache rule
  if (cacheRule) {
    // Set the "CDN-Cache-Control" header
    const header = useResponseHeader('CDN-Cache-Control')
    header.value = cacheRule
  }
})
